<template>
  <div class="main_div" :key="componentKey">
    <div style="height: auto">
      <n-space>
        <NCard style="margin-top: 20px; margin-left: 80px; border: 0px; min-width: 800px">
          <n-space item-style="display: flex;" align="center">
            <div style="font-size: 20px; width: 150px; font-weight: bold">개인 문의 관리</div>
          </n-space>
          <div class="content_delimiter_line"></div>

          <n-space vertical style="margin-top: 10px">
            <n-space align="center" item-style="display: flex;">
              <n-form-item label="검색" style="min-width: 350px" label-placement="left">
                <n-input-group>
                  <n-select v-model:value="searchType" :options="searchOptions" style="width: 120px" />
                  <n-input v-model:value="searchValue" placeholder="검색어를 입력하세요" clearable />
                </n-input-group>
              </n-form-item>

              <n-form-item label="등록일 선택" style="min-width: 300px" label-placement="left">
                <n-date-picker v-model:value="dateRange" type="daterange" clearable />
              </n-form-item>
              <n-form-item style="margin-bottom: 0" label-placement="left">
                <n-button type="primary" @click="wrappedRefreshMyregs" :disabled="!dateRange"> 조회 </n-button>
              </n-form-item>
            </n-space>
          </n-space>

          <Loading v-show="loading" />
          <div>
            <div v-if="listLoading" class="loading-indicator">개인 문의 목록을 불러오는 중...</div>
            <div v-if="error" class="error-message">{{ error }}</div>
            <div v-if="!listLoading && !error">
              <div class="table-container">
                <n-data-table
                  v-if="myregList.length > 0"
                  :columns="columns"
                  :data="myregList"
                  :pagination="false"
                  :bordered="true"
                />
                <p v-else>등록된 개인 문의가 없습니다.</p>
              </div>
              <div v-if="myregList.length > 0" class="pagination-controls" style="margin-top: 15px; text-align: center">
                <n-button @click="wrappedGoToPreviousPage" :disabled="currentPage === 1" style="margin-right: 8px">
                  이전 페이지
                </n-button>
                <span style="margin: 0 10px">{{ currentPage }} 페이지</span>
                <n-button @click="wrappedGoToNextPage" :disabled="!hasNextPage"> 다음 페이지 </n-button>
              </div>
            </div>
          </div>
        </NCard>
        <NModal
          v-model:show="isAlert"
          :mask-closable="false"
          :content="alertMessage"
          :type="alertType"
          preset="dialog"
          title="Dialog"
          positive-text="확인"
          @positive-click="onPositiveClick"
        >
        </NModal>
        <NModal v-model:show="isShowContent">
          <n-card style="width: 600px" title="내용" :bordered="false" size="huge" role="dialog" aria-modal="true">
            {{ contentMessage }}
          </n-card>
        </NModal>
        <NModal
          v-model:show="isDeleteConfirm"
          :mask-closable="false"
          :content="deleteConfirmMessage"
          :type="deleteConfirmType"
          preset="dialog"
          negative-text="취소"
          positive-text="확인"
          @positive-click="onDeletePositiveClick"
          @negative-click="onDeleteNegativeClick"
        >
        </NModal>
      </n-space>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, h } from "vue";
import { getCallable } from "@/config/env";
import {
  NDataTable,
  NSpace,
  NCheckbox,
  NCard,
  NSelect,
  NButton,
  NIcon,
  NModal,
  NRadio,
  NRadioGroup,
  NInput,
  FormInst,
  FormRules,
  useMessage,
  useNotification,
  useDialog,
  NInputGroup,
  NFormItem,
  NDatePicker,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import * as common from "@/assets/common/common";
import { Myreg } from "@/model/myreg";
import { QueryInfo } from "@/model/common";
import { usePagination } from "@/composables/usePagination";
import { useAuth } from "@/composables/useAuth";
const { loading, isLoggedIn } = useAuth();
import { useDownload } from "@/composables/useDownload";
const { downloadNormalFile } = useDownload();
const message = useMessage();
const notification = useNotification();
const dialog = useDialog();

const getMyregsCallable = getCallable("getMyregs");
const componentKey = ref(0);
const deleteConfirmMessage = ref<string | null>(null);
const contentMessage = ref<string | null>(null);
const alertMessage = ref<string | null>(null);
const alertType = ref<string | null>(null);
const isDeleteConfirm = ref<boolean>(false);
const deleteConfirmType = ref<string | null>(null);
const isShowContent = ref<boolean>(false);
const isAlert = ref<boolean>(false);

const dateRange = ref<[number, number] | null>(null);

const searchValue = ref<string | null>(null);
const searchType = ref("name");
const searchOptions = [
  { label: "이름", value: "name" },
  { label: "연락처", value: "phoneNumber" },
  { label: "이메일", value: "email" },
];

const {
  paginatedItems: myregList,
  listLoading,
  error,
  currentPage,
  hasNextPage,
  goToNextPage: composableGoToNextPage,
  goToPreviousPage: composableGoToPreviousPage,
  refreshData: composableRefreshMyregs,
} = usePagination<OrgMyreg, { startDate?: string; endDate?: string }>(
  async (params) => getMyregs(params),
  25 // 페이지 크기
);

const getMyregs = async (params: any) => {
  if (searchValue.value && searchValue.value.trim() !== "") {
    params.searchKey = searchType.value;
    params.searchValue = searchValue.value.trim();
  }
  return await getMyregsCallable(params);
};

async function wrappedRefreshMyregs() {
  loading.value = true; // 전역 로딩 시작

  if (!dateRange.value) {
    message.error("기간을 선택해주세요.");
    return;
  }
  const [startTimestamp, endTimestamp] = dateRange.value;
  const startDate = startTimestamp.toString();
  const endDate = common.addDaysToTimestamp(endTimestamp, 1).toString();

  await composableRefreshMyregs({ startDate, endDate });
  loading.value = false; // 전역 로딩 종료
}

const columns = [
  {
    title: "순번",
    key: "index",
    render(_row: Myreg, rowIndex: number) {
      return rowIndex + 1;
    },
  },
  {
    title: "이름",
    key: "name",
    resizable: true,
  },
  {
    title: "연락처",
    key: "phoneNumber",
    resizable: true,
  },
  {
    title: "내용",
    sorter: "default",
    render(row: Myreg) {
      return h(
        NButton,
        {
          size: "small",
          onClick: () => {
            onShowContent(row);
          },
        },
        { default: () => common.parseContent(row.content) }
      );
    },
  },
  {
    title: "첨부파일",
    key: "attached_file_drive_id",
    render(row: Board) {
      return h(
        NButton,
        {
          size: "small",
          onClick: () => {
            onFileDonwload(row);
          },
          bordered: true,
        },
        { default: () => "다운로드" }
      );
    },
  },
  {
    title: "이메일",
    key: "email",
    resizable: true,
  },
  {
    title: "등록일",
    key: "createdDate",
    render(row: Myreg) {
      return common.getDate(row.createdDate);
    },
  },
];
async function onFileDonwload(row: Board) {
  loading.value = true;
  await downloadNormalFile(row.attached_file_drive_id, row.attached_file_name);
  loading.value = false;
}
function onShowContent(row: Myreg) {
  isShowContent.value = true;
  contentMessage.value = row.content;
}
function onPositiveClick() {
  isAlert.value = false;
}

async function wrappedGoToNextPage() {
  if (hasNextPage.value) {
    await composableGoToNextPage();
  }
}

async function wrappedGoToPreviousPage() {
  if (currentPage.value > 1) {
    await composableGoToPreviousPage();
  }
}

onMounted(() => {
  // wrappedRefreshMyregs();
});
</script>

<style scoped></style>
